import React, { useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Check, Crown, Zap, X, ArrowLeft, Copy, CheckCircle2, Clock, CreditCard, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { useRazorpay } from "react-razorpay";

const UPI_ID = "9500129965@jupiteraxis";
const UPI_NAME = "Resumiq.in";

const PLANS = [
  {
    id: 'weekly', name: 'Weekly', amount: 350,
    priceINR: '₹350', priceUSD: '$4', period: '/week',
    highlight: false, badge: 'Try it out',
    features: [
      'Unlimited generations',
      'All 4 generation modes',
      '6 PDF templates',
      'Full application history',
      'ATS keyword analysis',
    ]
  },
  {
    id: 'pro', name: 'Pro', amount: 999,
    priceINR: '₹999', priceUSD: '$12', period: '/mo',
    highlight: true, badge: 'Most Popular',
    features: [
      'Unlimited generations',
      'All 4 generation modes',
      '6 PDF templates',
      'Full application history',
      'Interview prep questions',
      'LinkedIn bio + cold DMs',
      'ATS keyword analysis',
    ]
  },
  {
    id: 'career', name: 'Career', amount: 1999,
    priceINR: '₹1,999', priceUSD: '$24', period: '/mo',
    highlight: false, badge: null,
    features: [
      'Everything in Pro',
      'Bulk generate (10 jobs)',
      'Job application tracker',
      'Recruiter cold email writer',
      'Naukri profile optimizer',
      'Priority support',
    ]
  }
];

export default function UpgradeModal({ onClose }) {
  const { user, refreshUser } = useAuth();
  const { Razorpay } = useRazorpay();
  const [step, setStep] = useState('plans'); // plans | method | upi | submitted | success
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setStep('method');
  };

  const copyUPI = () => {
    navigator.clipboard.writeText(UPI_ID);
    setCopied(true);
    toast.success('UPI ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // UPI manual payment submission
  const handleSubmitUPI = async () => {
    if (!transactionId.trim()) { toast.error('Please enter your transaction/UTR ID'); return; }
    if (transactionId.trim().length < 6) { toast.error('Please enter a valid transaction ID'); return; }
    setSubmitting(true);
    try {
      await api.submitPayment({
        transaction_id: transactionId.trim(),
        plan: selectedPlan.id,
        amount: selectedPlan.amount
      });
      setStep('submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Razorpay payment
  const handleRazorpay = useCallback(async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    try {
      const { data } = await api.razorpayCreateOrder({ plan: selectedPlan.id });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Resumiq.in",
        description: `${selectedPlan.name} Plan - ${selectedPlan.id === 'weekly' ? 'Weekly' : 'Monthly'}`,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            await api.razorpayVerify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`${selectedPlan.name} plan activated!`);
            if (refreshUser) await refreshUser();
            setStep('success');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
        },
        theme: {
          color: "#c8a96e",
        },
        modal: {
          ondismiss: () => setProcessing(false),
        }
      };

      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(response.error?.description || "Payment failed. Please try again.");
        setProcessing(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  }, [Razorpay, selectedPlan, user, refreshUser]);

  const upiUrl = selectedPlan
    ? `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(UPI_NAME)}&am=${selectedPlan.amount}&cu=INR&tn=${encodeURIComponent(`${selectedPlan.name} Plan - Resumiq.in`)}`
    : '';

  const goBack = () => {
    if (step === 'method') setStep('plans');
    else if (step === 'upi') setStep('method');
  };

  return (
    <div data-testid="upgrade-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(8px)', background: 'rgba(10,10,15,0.7)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-3xl bg-[#13131a] border border-[#2a2a38] rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2a2a38]">
          <div className="flex items-center gap-3">
            {(step === 'method' || step === 'upi') && (
              <button onClick={goBack} className="text-[#8a8799] hover:text-[#f0ede8] transition-colors">
                <ArrowLeft size={16} />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <Crown size={14} className="text-[#c8a96e]" />
                <span className="text-[0.65rem] font-medium text-[#c8a96e] uppercase tracking-wider">
                  {step === 'plans' ? 'Upgrade Plan' : step === 'method' ? 'Choose Payment' : step === 'upi' ? 'UPI Payment' : step === 'success' ? 'Plan Activated' : 'Payment Submitted'}
                </span>
              </div>
              <h2 className="text-lg font-bold text-[#f0ede8]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {step === 'plans' ? 'Unlock your full potential' : step === 'method' ? `Upgrade to ${selectedPlan?.name}` : step === 'upi' ? `Pay ₹${selectedPlan?.amount?.toLocaleString()}` : step === 'success' ? 'Welcome to ' + selectedPlan?.name + '!' : 'Thank you!'}
              </h2>
            </div>
          </div>
          <button data-testid="close-upgrade" onClick={onClose} className="text-[#8a8799] hover:text-[#f0ede8] transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Step 1: Plan Selection */}
        {step === 'plans' && (
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PLANS.map(plan => (
                <div key={plan.id} data-testid={`plan-card-${plan.id}`}
                  className={`relative p-5 rounded-lg border transition-all duration-300 ${
                    plan.highlight ? 'border-[#c8a96e] bg-[#1c1c26]' : 'border-[#2a2a38] bg-[#1c1c26] hover:border-[#2a2a38]/80'
                  }`}
                  style={plan.highlight ? { boxShadow: '0 0 30px -10px rgba(200,169,110,0.3)' } : {}}>
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8a96e] text-black text-[0.65rem] px-3 py-0.5 rounded-full font-semibold">
                      {plan.badge}
                    </div>
                  )}
                  <h3 className="text-base font-bold text-[#f0ede8]" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mt-1 mb-4">
                    <span className="text-2xl font-bold text-[#c8a96e]">{plan.priceINR}</span>
                    <span className="text-[#8a8799] text-xs">{plan.period}</span>
                  </div>
                  <ul className="space-y-2 mb-5">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-[#f0ede8]">
                        <Check size={12} className="text-[#4ade80] flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`upgrade-btn-${plan.id}`} onClick={() => handleSelectPlan(plan)}
                    className={`w-full py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      plan.highlight ? 'bg-[#c8a96e] text-black hover:bg-[#e8c98e]' : 'border border-[#c8a96e] text-[#c8a96e] hover:bg-[#c8a96e]/10'
                    }`}>
                    <Zap size={12} className="inline mr-1.5" /> Get {plan.name} — {plan.priceINR}{plan.period}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[#8a8799] text-[0.65rem] text-center mt-4">Secure payment · Cards, UPI, Netbanking supported · Cancel anytime</p>
          </div>
        )}

        {/* Step 2: Payment Method Selection */}
        {step === 'method' && selectedPlan && (
          <div className="p-5 space-y-4">
            <div className="bg-[#0a0a0f] border border-[#2a2a38] rounded-lg p-4 text-center">
              <p className="text-[#8a8799] text-[0.6rem] uppercase tracking-wider">Amount to Pay</p>
              <p className="text-2xl font-bold text-[#c8a96e] mt-0.5" style={{ fontFamily: 'Playfair Display, serif' }}>
                ₹{selectedPlan.amount.toLocaleString()}
              </p>
              <p className="text-[#8a8799] text-[0.6rem] mt-0.5">{selectedPlan.name} Plan · {selectedPlan.id === 'weekly' ? 'Weekly' : 'Monthly'}</p>
            </div>

            {/* Razorpay - Primary */}
            <button
              data-testid="pay-razorpay-btn"
              onClick={handleRazorpay}
              disabled={processing}
              className="w-full p-4 rounded-lg border-2 border-[#c8a96e] bg-[#c8a96e]/5 hover:bg-[#c8a96e]/10 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#c8a96e]/15 flex items-center justify-center flex-shrink-0">
                  <CreditCard size={18} className="text-[#c8a96e]" />
                </div>
                <div className="flex-1">
                  <p className="text-[#f0ede8] text-sm font-semibold flex items-center gap-2">
                    Pay with Razorpay
                    <span className="text-[0.55rem] bg-[#4ade80]/15 text-[#4ade80] px-1.5 py-0.5 rounded font-medium">RECOMMENDED</span>
                  </p>
                  <p className="text-[#8a8799] text-[0.65rem] mt-0.5">Cards, UPI, Netbanking, Wallets · Instant activation</p>
                </div>
                <Zap size={14} className="text-[#c8a96e] flex-shrink-0" />
              </div>
              {processing && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-[#c8a96e] border-t-transparent rounded-full animate-spin" />
                  <span className="text-[#c8a96e] text-xs">Opening payment window...</span>
                </div>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#2a2a38]" />
              <span className="text-[#8a8799] text-[0.6rem]">OR</span>
              <div className="flex-1 h-px bg-[#2a2a38]" />
            </div>

            {/* Manual UPI */}
            <button
              data-testid="pay-upi-manual-btn"
              onClick={() => setStep('upi')}
              className="w-full p-4 rounded-lg border border-[#2a2a38] hover:border-[#c8a96e]/40 transition-all duration-200 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1c1c26] flex items-center justify-center flex-shrink-0">
                  <Smartphone size={18} className="text-[#8a8799]" />
                </div>
                <div className="flex-1">
                  <p className="text-[#f0ede8] text-sm font-medium">Pay via UPI QR Code</p>
                  <p className="text-[#8a8799] text-[0.65rem] mt-0.5">Scan & pay manually · Admin verifies within minutes</p>
                </div>
                <ArrowLeft size={14} className="text-[#8a8799] rotate-180 flex-shrink-0" />
              </div>
            </button>
          </div>
        )}

        {/* Step 3: UPI Manual Payment */}
        {step === 'upi' && selectedPlan && (
          <div className="p-5">
            <div className="flex flex-col md:flex-row gap-5">
              {/* QR Code Section */}
              <div className="flex-1 flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg mb-3">
                  <QRCodeSVG value={upiUrl} size={180} level="H" includeMargin={false}
                    bgColor="#ffffff" fgColor="#000000" />
                </div>
                <p className="text-[#f0ede8] text-xs font-medium mb-1">Scan with any UPI app</p>
                <p className="text-[#8a8799] text-[0.6rem]">GPay · PhonePe · Paytm · Any UPI app</p>

                <div className="mt-3 bg-[#0a0a0f] border border-[#2a2a38] rounded-lg p-3 w-full text-center">
                  <p className="text-[#8a8799] text-[0.6rem] uppercase tracking-wider">Amount to Pay</p>
                  <p className="text-2xl font-bold text-[#c8a96e] mt-0.5" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ₹{selectedPlan.amount.toLocaleString()}
                  </p>
                </div>

                <div className="mt-3 w-full">
                  <p className="text-[#8a8799] text-[0.6rem] mb-1 text-center">Or pay directly to UPI ID:</p>
                  <button data-testid="copy-upi-id" onClick={copyUPI}
                    className="w-full flex items-center justify-center gap-2 bg-[#0a0a0f] border border-[#2a2a38] rounded-lg px-3 py-2.5 hover:border-[#c8a96e]/40 transition-colors">
                    <span className="text-[#f0ede8] text-xs font-mono">{UPI_ID}</span>
                    {copied ? <CheckCircle2 size={13} className="text-[#4ade80]" /> : <Copy size={13} className="text-[#8a8799]" />}
                  </button>
                </div>
              </div>

              {/* Transaction ID Entry */}
              <div className="flex-1 flex flex-col">
                <div className="bg-[#0a0a0f] border border-[#2a2a38] rounded-lg p-4 flex-1">
                  <h4 className="text-[#f0ede8] text-xs font-semibold mb-1">After Payment</h4>
                  <p className="text-[#8a8799] text-[0.6rem] mb-4 leading-relaxed">
                    Enter your UPI Transaction ID (UTR number) below. You'll find it in your payment app's transaction details.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-[0.6rem] text-[#8a8799]">
                      <div className="w-5 h-5 rounded-full bg-[#c8a96e]/10 text-[#c8a96e] flex items-center justify-center text-[0.55rem] font-bold flex-shrink-0">1</div>
                      Scan QR or pay to UPI ID
                    </div>
                    <div className="flex items-center gap-2 text-[0.6rem] text-[#8a8799]">
                      <div className="w-5 h-5 rounded-full bg-[#c8a96e]/10 text-[#c8a96e] flex items-center justify-center text-[0.55rem] font-bold flex-shrink-0">2</div>
                      Copy UTR/Transaction ID from receipt
                    </div>
                    <div className="flex items-center gap-2 text-[0.6rem] text-[#8a8799]">
                      <div className="w-5 h-5 rounded-full bg-[#c8a96e]/10 text-[#c8a96e] flex items-center justify-center text-[0.55rem] font-bold flex-shrink-0">3</div>
                      Paste below and submit
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-[#8a8799] text-[0.6rem] mb-1 block">Transaction/UTR ID</label>
                    <input data-testid="transaction-id-input" type="text" value={transactionId}
                      onChange={e => setTransactionId(e.target.value)}
                      placeholder="e.g., 412345678901"
                      className="w-full bg-[#13131a] border border-[#2a2a38] rounded-lg px-3 py-2.5 text-sm text-[#f0ede8] focus:border-[#c8a96e] outline-none font-mono placeholder:text-[#8a8799]/40" />
                  </div>
                  <button data-testid="submit-payment-btn" onClick={handleSubmitUPI} disabled={submitting || !transactionId.trim()}
                    className="w-full mt-4 py-2.5 bg-[#c8a96e] text-black text-xs font-semibold rounded-lg hover:bg-[#e8c98e] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? 'Submitting...' : 'Confirm Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step: UPI Submitted (pending admin verification) */}
        {step === 'submitted' && (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#f59e0b]/10 flex items-center justify-center mx-auto mb-4">
              <Clock size={24} className="text-[#f59e0b]" />
            </div>
            <h3 className="text-lg font-bold text-[#f0ede8] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Submitted
            </h3>
            <p className="text-[#8a8799] text-xs leading-relaxed max-w-sm mx-auto mb-5">
              Your payment is being verified. Your plan will be activated within a few minutes once confirmed.
            </p>
            <div className="bg-[#0a0a0f] border border-[#2a2a38] rounded-lg p-4 inline-block mb-5">
              <div className="flex items-center gap-4 text-xs">
                <div><p className="text-[#8a8799] text-[0.6rem]">Plan</p><p className="text-[#f0ede8] font-medium">{selectedPlan?.name}</p></div>
                <div className="w-px h-8 bg-[#2a2a38]" />
                <div><p className="text-[#8a8799] text-[0.6rem]">Amount</p><p className="text-[#c8a96e] font-medium">₹{selectedPlan?.amount.toLocaleString()}</p></div>
                <div className="w-px h-8 bg-[#2a2a38]" />
                <div><p className="text-[#8a8799] text-[0.6rem]">Status</p><p className="text-[#f59e0b] font-medium">Pending</p></div>
              </div>
            </div>
            <div>
              <button data-testid="close-payment-submitted" onClick={onClose}
                className="px-6 py-2.5 bg-[#c8a96e] text-black text-xs font-semibold rounded-lg hover:bg-[#e8c98e] transition-colors">
                Got it, close
              </button>
            </div>
          </div>
        )}

        {/* Step: Razorpay Success (instant activation) */}
        {step === 'success' && (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#4ade80]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#4ade80]" />
            </div>
            <h3 className="text-lg font-bold text-[#f0ede8] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {selectedPlan?.name} Plan Activated!
            </h3>
            <p className="text-[#8a8799] text-xs leading-relaxed max-w-sm mx-auto mb-5">
              Your payment was successful. You now have access to all {selectedPlan?.name} features. Start generating unlimited resumes!
            </p>
            <div className="bg-[#0a0a0f] border border-[#2a2a38] rounded-lg p-4 inline-block mb-5">
              <div className="flex items-center gap-4 text-xs">
                <div><p className="text-[#8a8799] text-[0.6rem]">Plan</p><p className="text-[#4ade80] font-medium">{selectedPlan?.name}</p></div>
                <div className="w-px h-8 bg-[#2a2a38]" />
                <div><p className="text-[#8a8799] text-[0.6rem]">Amount</p><p className="text-[#c8a96e] font-medium">₹{selectedPlan?.amount.toLocaleString()}</p></div>
                <div className="w-px h-8 bg-[#2a2a38]" />
                <div><p className="text-[#8a8799] text-[0.6rem]">Status</p><p className="text-[#4ade80] font-medium">Active</p></div>
              </div>
            </div>
            <div>
              <button data-testid="close-payment-success" onClick={onClose}
                className="px-6 py-2.5 bg-[#c8a96e] text-black text-xs font-semibold rounded-lg hover:bg-[#e8c98e] transition-colors">
                Start Generating
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
